import { ApolloError } from '@apollo/client';
import {
  useGetOfferScorecardsForOrgQuery,
  useGetOfferScorecardsMetricsForOrgQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  GetOfferScorecardsMetricsForOrgQuery,
  TeamLeadOfferScorecardRowFragment,
} from '@client/graphql/__generated__/types';
import { createContext, useContext, useEffect, useState } from 'react';
import { useInfiniteQuery } from '~/services/document/hooks/useInfiniteQuery';

interface OfferScorecardsContextValue {
  scorecard: {
    rows: TeamLeadOfferScorecardRowFragment[];
    loading: boolean;
    error?: ApolloError;
    fetchTriggerRef: (ref: HTMLDivElement) => void;
  };
  metrics: {
    data?: GetOfferScorecardsMetricsForOrgQuery['offerScorecardsMetricsForOrg'];
    loading: boolean;
    error?: ApolloError;
  };
}

export const OfferScorecardsContext =
  createContext<OfferScorecardsContextValue>({} as OfferScorecardsContextValue);

export const OfferScorecardsForOrgContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [offerScorecardRows, setOfferScorecardRows] = useState<
    TeamLeadOfferScorecardRowFragment[]
  >([]);

  const [
    fetchOfferScorecardsTriggerRef,
    { data, loading: offerScorecardsLoading, error: offerScorecardsError },
  ] = useInfiniteQuery(useGetOfferScorecardsForOrgQuery, {
    variables: {
      filters: {},
    },
  });

  const {
    data: offerScorecardsMetricsData,
    loading: offerScorecardsMetricsLoading,
    error: offerScorecardsMetricsError,
  } = useGetOfferScorecardsMetricsForOrgQuery();

  useEffect(() => {
    if (!offerScorecardsLoading) {
      setOfferScorecardRows(data?.offerScorecardsForOrg?.results || []);
    }
  }, [data, offerScorecardsLoading]);

  const value: OfferScorecardsContextValue = {
    scorecard: {
      rows: offerScorecardRows,
      loading: offerScorecardsLoading,
      error: offerScorecardsError,
      fetchTriggerRef: fetchOfferScorecardsTriggerRef,
    },
    metrics: {
      data: offerScorecardsMetricsData?.offerScorecardsMetricsForOrg,
      loading: offerScorecardsMetricsLoading,
      error: offerScorecardsMetricsError,
    },
  };

  return (
    <OfferScorecardsContext.Provider value={value}>
      {children}
    </OfferScorecardsContext.Provider>
  );
};

export const useOfferScorecardsForOrgContext = () => {
  const context = useContext(OfferScorecardsContext);
  if (context === undefined) {
    throw new Error(
      'useOfferScorecardsForOrgContext must be used within an OfferScorecardsForOrgContextProvider'
    );
  }

  return context;
};
